import { type Carer, UserRole } from '@lmc/ts-common'
import { Button, Heading, NavigationModal } from '@lmc/ui-kit'
import { Form as AntdForm, Divider, Form } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { capitalize } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import ModalLayout from 'screens/Rostering/components/ModalLayout'
import {
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY
} from 'screens/Rostering/hooks/queryConstants'
import useAllocationDoubleBookings from 'screens/Rostering/hooks/useAllocationDoubleBookings'
import type { UpdateRevisionPayload } from 'screens/Rostering/hooks/useRevisionUpdate'
import DoubleBookedModal from 'screens/Rostering/RotaBuilder/screens/AllocationWindow/components/DoubleBookedModal'
import {
  AboutShiftSection,
  AboutVisitSection,
  NeedsAssignedSection,
  SkillsRequiredSection
} from 'screens/Rostering/RotaPattern/sections'
import { RotaInstanceStatus } from 'screens/Rostering/types'
import { styled } from 'styled-components'
import { getRevisionReqFormInitialValues } from '../utils'

type EditReqModalProps = {
  editModalVisible: boolean
  onCancel: () => void
  revisionDetail: any
  updateRevision?: (values: UpdateRevisionPayload) => Promise<any>
  isLoadingUpdate?: boolean
}

const EditReqModal = ({
  editModalVisible,
  onCancel,
  revisionDetail,
  updateRevision,
  isLoadingUpdate
}: EditReqModalProps) => {
  const [form] = AntdForm.useForm()
  const queryClient = useQueryClient()

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )

  const isCareHomeAdmin = userRole === UserRole.careHomeAdmin

  const initialValues = getRevisionReqFormInitialValues(revisionDetail)

  // Extract original team member IDs from revisionDetail for validation
  const originalTeamMemberIds = (revisionDetail?.teamMembers || []).map(
    (member: any) => member.teamMemberId
  )

  const [formData, setFormData] = useState<Record<string, any> | undefined>()

  useEffect(() => {
    if (editModalVisible) {
      // Include original team member IDs, clockIns, status, and id in formData for validation and filtering
      const formDataWithOriginals = {
        ...initialValues,
        originalTeamMemberIds,
        clockIns: revisionDetail?.clockIns,
        status: revisionDetail?.status,
        id: revisionDetail?.id,
        eventId: revisionDetail?.id
      }
      setFormData(formDataWithOriginals)
      form.setFieldsValue(initialValues)
    }
  }, [revisionDetail, editModalVisible])

  const [isDirty, setIsDirty] = useState(false)

  const [showPrompt, setShowPrompt] = useState(false)
  const [showDoubleBookedPrompt, setShowDoubleBookedPrompt] = useState(false)
  const [showStatusPrompt, setShowStatusPrompt] = useState(false)
  const [doubleBook, setDoubleBook] = useState(false)
  const [pendingUpdate, setPendingUpdate] =
    useState<UpdateRevisionPayload | null>(null)

  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: ['rota_admin_team_member_edit']
  })?.data

  const flagsRotaAdminTeamMemberEdit =
    flags?.rota_admin_team_member_edit?.enabled

  const isDone = revisionDetail?.status === RotaInstanceStatus.DONE

  const canEditByAdmin =
    flagsRotaAdminTeamMemberEdit && isCareHomeAdmin && isDone

  const { doubleBookings } = useAllocationDoubleBookings()

  const handleCancel = () => {
    if (isDirty) {
      setShowPrompt(true)
      return
    }
    handleConfirmCancel()
  }

  const handleConfirmCancel = () => {
    onCancel()
    setFormData(undefined)
    form.setFieldsValue(initialValues)
  }

  const handleSave = async () => {
    if (isDirty && canEditByAdmin) {
      setShowStatusPrompt(true)
      return
    }

    if (isDirty) {
      await form.validateFields()
      form.submit()
      return
    }

    if (!isLoadingUpdate) handleConfirmCancel()
  }

  const onSave = async () => {
    if (isDirty) {
      await form.validateFields()
      form.submit()
      return
    }

    if (!isLoadingUpdate) handleConfirmCancel()
  }

  const handleFormValuesChange = (values: Record<string, any>) => {
    if (values) {
      setIsDirty(true)
    }

    setFormData((prev) => ({
      ...prev,
      ...values,
      // Preserve originalTeamMemberIds, clockIns, status, and id when form data changes
      originalTeamMemberIds:
        prev?.originalTeamMemberIds || originalTeamMemberIds,
      clockIns: prev?.clockIns ?? revisionDetail?.clockIns,
      status: prev?.status ?? revisionDetail?.status,
      id: prev?.id ?? revisionDetail?.id,
      eventId: prev?.eventId ?? revisionDetail?.id
    }))
  }

  const handleOnUpdateFinish = async (values: Record<string, any>) => {
    if (isDirty) {
      const { teamMembers, ...otherValues } = values

      const changedValues = Object.entries(otherValues).reduce(
        (acc, [key, value]) => {
          const initialValue = initialValues[key]
          if (JSON.stringify(value) !== JSON.stringify(initialValue)) {
            acc[key] = value
          }
          return acc
        },
        {} as Record<string, any>
      )

      const updatePayload: UpdateRevisionPayload = {
        eventId: revisionDetail?.id,
        ...changedValues
      }

      let addedIds: string[] = []
      let removedIds: string[] = []
      let teamMembersDoubleBooked: string[] = []

      if (teamMembers) {
        const originalTeamMemberIds = (revisionDetail?.teamMembers || []).map(
          (member: any) => member.teamMemberId
        )

        const normalizedTeamMembers = Array.isArray(teamMembers)
          ? teamMembers.map((member) =>
              typeof member === 'object' ? member.value : member
            )
          : []

        addedIds = normalizedTeamMembers.filter(
          (id: string) => !originalTeamMemberIds.includes(id)
        )
        removedIds = originalTeamMemberIds.filter(
          (id: string) => !normalizedTeamMembers.includes(id)
        )

        // Check for double bookings if team members are being added
        if (addedIds.length > 0) {
          teamMembersDoubleBooked = await doubleBookings({
            id: revisionDetail?.id,
            teamMembers: addedIds
          })

          // If there are double bookings and we haven't shown the prompt yet, show it
          if (!showDoubleBookedPrompt && teamMembersDoubleBooked?.length > 0) {
            updatePayload.teamMembers = {
              add: addedIds.map((id: string) => ({
                teamMemberId: id,
                role: 'carer'
              })),
              remove: removedIds,
              doubleBooked: teamMembersDoubleBooked,
              allowDoubleBooking: false
            }
            setPendingUpdate(updatePayload)
            setShowDoubleBookedPrompt(true)
            return
          }
        }

        if (addedIds.length > 0 || removedIds.length > 0) {
          updatePayload.teamMembers = {
            add: addedIds.map((id: string) => ({
              teamMemberId: id,
              role: 'carer'
            })),
            remove: removedIds,
            doubleBooked: teamMembersDoubleBooked,
            allowDoubleBooking: doubleBook
          }
        }
      }

      if (revisionDetail?.siteShift && updatePayload.addressId) {
        updatePayload.addressId = undefined
      }

      const updateRes = await updateRevision?.(updatePayload)

      if (updateRes) {
        queryClient.invalidateQueries([REVISION_QUERY_KEY, revisionDetail?.id])
        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        setPendingUpdate(null)
        setShowDoubleBookedPrompt(false)
        setDoubleBook(false)
        handleConfirmCancel()
      }
    }
  }

  const handleDoubleBookedConfirm = async (allowDoubleBooking: boolean) => {
    setDoubleBook(allowDoubleBooking)

    if (pendingUpdate && pendingUpdate.teamMembers) {
      pendingUpdate.teamMembers.allowDoubleBooking = allowDoubleBooking

      const updateRes = await updateRevision?.(pendingUpdate)

      if (updateRes) {
        queryClient.invalidateQueries([REVISION_QUERY_KEY, revisionDetail?.id])
        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        setPendingUpdate(null)
        setShowDoubleBookedPrompt(false)
        setDoubleBook(false)
        handleConfirmCancel()
      }
    }
  }

  return (
    <ModalLayout
      open={editModalVisible}
      onCancel={handleCancel}
      headerHeight={8}
      footerContent={
        <FooterWrapper>
          <FooterRightSide>
            <Button onClick={handleCancel}>{t('CANCEL')}</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isLoadingUpdate}
            >
              {t('SAVE')}
            </Button>
          </FooterRightSide>
        </FooterWrapper>
      }
      headerContent={
        <>
          <StyledHeading level={2}>{t('EDIT_REQ')}</StyledHeading>
          <StyledDivider />
        </>
      }
    >
      <StyledForm
        form={form}
        onFinish={handleOnUpdateFinish}
        onValuesChange={handleFormValuesChange}
        initialValues={initialValues}
      >
        {!revisionDetail?.siteShift && (
          <NeedsAssignedSection formData={formData} />
        )}
        <SkillsRequiredSection formData={formData} />
        {revisionDetail?.siteShift ? (
          <AboutShiftSection
            hideAboutSection
            formData={formData}
            showTeamMembersSelect
          />
        ) : (
          <AboutVisitSection
            hideAboutSection
            formData={formData}
            showTeamMembersSelect
          />
        )}
      </StyledForm>
      <NavigationModal
        showPrompt={showPrompt}
        modalTitle={t('ARE_YOU_SURE')}
        modalContentText={t('EXIT_EVENT')}
        modalConfirmText={t('EXIT')}
        modalCancelText={t('CANCEL')}
        confirmNavigation={() => {
          setShowPrompt(false)
          setIsDirty(false)
          handleConfirmCancel()
        }}
        cancelNavigation={() => setShowPrompt(false)}
      />
      {flagsRotaAdminTeamMemberEdit && (
        <NavigationModal
          showPrompt={showStatusPrompt}
          modalTitle={t('ARE_YOU_SURE_CONTINUE')}
          modalContentText={t('EDIT_EVENT_IN_STATUS', {
            eventStatus: capitalize(revisionDetail?.status)
          })}
          modalConfirmText={t('DONE', { defaultValue: 'Done' })}
          modalCancelText={t('CANCEL')}
          confirmNavigation={() => {
            setShowStatusPrompt(false)
            onSave()
          }}
          cancelNavigation={() => setShowStatusPrompt(false)}
          confirmButtonProps={{ variant: 'primary' }}
        />
      )}
      <DoubleBookedModal
        showPrompt={showDoubleBookedPrompt}
        setShowPrompt={setShowDoubleBookedPrompt}
        confirmNavigation={(allowDoubleBooking) =>
          handleDoubleBookedConfirm(allowDoubleBooking ?? false)
        }
        isDoubleBookedSelected={doubleBook}
        setIsDoubleBookedSelected={setDoubleBook}
        cancelNavigation={() => {
          setShowDoubleBookedPrompt(false)
          setPendingUpdate(null)
          setDoubleBook(false)
          // Reset form to initial values
          form.setFieldsValue(initialValues)
          setFormData(initialValues)
          setIsDirty(false)
        }}
      />
    </ModalLayout>
  )
}
const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  font-size: 24px;
`
const StyledDivider = styled(Divider)`
  margin: 16px 0;
`
const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`
const FooterRightSide = styled.div`
  display: flex;
  gap: 8px;
`
const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 10px;
` as unknown as typeof Form

export default EditReqModal
